import { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Send, Sparkles, Package, Mic, MicOff } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const GREETING = "Hi, I'm Turbo — Turbo Transit's assistant. If you have any questions, go ahead and ask me. I can help you get a quote, track a package, or point you to the right service.";
const QUICK = [
  { label: "Track a package", prompt: "I want to track a package" },
  { label: "Get a quote", prompt: "How do I get a shipping quote?" },
  { label: "Speak to the team", prompt: "How do I contact your team?" },
];

const FloatingAssistant = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([{ role: "bot", text: GREETING }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceUnsupported, setVoiceUnsupported] = useState(false);
  const scrollRef = useRef(null);
  const recogRef = useRef(null);

  // Setup Web Speech API once
  useEffect(() => {
    const SR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
    if (!SR) { setVoiceUnsupported(true); return; }
    const r = new SR();
    r.continuous = false;
    r.interimResults = true;
    r.lang = "en-US";
    r.onresult = (event) => {
      let txt = "";
      for (let i = event.resultIndex; i < event.results.length; i++) txt += event.results[i][0].transcript;
      setInput(txt);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
    return () => { try { r.abort(); } catch {} };
  }, []);

  const toggleMic = () => {
    if (voiceUnsupported || !recogRef.current) return;
    if (listening) {
      recogRef.current.stop();
      setListening(false);
    } else {
      setInput("");
      try { recogRef.current.start(); setListening(true); } catch {}
    }
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((p) => [...p, { role: "user", text: q }]);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/assist/chat`, { message: q, session_id: sessionId });
      if (r.data.session_id && !sessionId) setSessionId(r.data.session_id);
      setMsgs((p) => [...p, { role: "bot", text: r.data.reply, kind: r.data.kind, tracking: r.data.tracking }]);
    } catch (e) {
      setMsgs((p) => [...p, { role: "bot", text: "I hit a snag — try again in a moment, or reach us at +1 (248) 828-5645." }]);
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      {/* Floating button — hidden when open on mobile to save space */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="assistant-toggle"
        aria-label={open ? "Close assistant" : "Open Turbo assistant"}
        className={`fixed z-40 bottom-5 right-5 rounded-full shadow-2xl transition-all ${open ? "hidden md:flex md:opacity-0 md:pointer-events-none" : "flex"}`}
        style={{ background: "linear-gradient(135deg, var(--tt-green) 0%, var(--tt-zam-orange) 100%)" }}
      >
        <span className="grid place-items-center h-14 w-14 md:h-16 md:w-16 rounded-full text-white hover:scale-105 transition">
          <MessageCircle className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2.4} />
        </span>
        <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 animate-pulse ring-2 ring-white" />
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed z-50 bottom-0 right-0 md:bottom-5 md:right-5 w-full md:w-[380px] h-[80vh] md:h-[560px] md:rounded-3xl bg-white shadow-2xl border border-[var(--tt-line)] flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between text-white" style={{ background: "linear-gradient(135deg, var(--tt-green) 0%, var(--tt-zam-orange) 100%)" }}>
            <div className="flex items-center gap-2.5">
              <div className="grid place-items-center h-9 w-9 rounded-xl bg-white/20 backdrop-blur-sm">
                <Sparkles className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-display font-bold">Turbo</div>
                <div className="text-[10px] uppercase tracking-widest opacity-90">Turbo Transit assistant</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="assistant-close" className="hover:bg-white/20 rounded-full p-1.5 transition"><X className="h-5 w-5" /></button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[var(--tt-cream)]" data-testid="assistant-messages">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`msg-${m.role}-${i}`}
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                    m.role === "user"
                      ? "bg-[var(--tt-navy)] text-white rounded-br-sm"
                      : "bg-white border border-[var(--tt-line)] text-[var(--tt-ink-soft)] rounded-bl-sm"
                  }`}
                >
                  {m.kind === "tracking" && m.tracking && !m.tracking.error && (
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--tt-zam-orange)] font-semibold mb-1">
                      <Package className="h-3 w-3" /> package status
                    </div>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: m.text.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>') }} />
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-white border border-[var(--tt-line)] px-3.5 py-2.5 shadow-sm">
                  <div className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-green)] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-green)] animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-[var(--tt-green)] animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick actions */}
          {msgs.length <= 2 && (
            <div className="px-4 py-2 flex flex-wrap gap-1.5 border-t border-[var(--tt-line)] bg-white">
              {QUICK.map((q) => (
                <button key={q.label} onClick={() => send(q.prompt)} data-testid={`assistant-quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`} className="text-xs rounded-full border border-[var(--tt-line)] px-3 py-1 hover:border-[var(--tt-green)] hover:text-[var(--tt-green)] transition">{q.label}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-[var(--tt-line)] bg-white p-3 flex gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              rows={1}
              data-testid="assistant-input"
              placeholder={listening ? "Listening… speak now" : "Ask a question or paste a tracking number…"}
              className={`flex-1 resize-none rounded-2xl border px-3 py-2 text-sm focus:outline-none transition max-h-24 ${listening ? "border-[var(--tt-zam-orange)] bg-orange-50" : "border-[var(--tt-line)] focus:border-[var(--tt-green)]"}`}
            />
            {!voiceUnsupported && (
              <button
                onClick={toggleMic}
                data-testid="assistant-mic"
                aria-label={listening ? "Stop listening" : "Start voice input"}
                className={`grid place-items-center h-10 w-10 rounded-full transition flex-shrink-0 ${listening ? "bg-[var(--tt-zam-orange)] text-white animate-pulse" : "border border-[var(--tt-line)] text-[var(--tt-navy)] hover:border-[var(--tt-green)] hover:text-[var(--tt-green)]"}`}
              >
                {listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </button>
            )}
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="assistant-send"
              aria-label="Send message"
              className="grid place-items-center h-10 w-10 rounded-full bg-[var(--tt-green)] text-white hover:brightness-110 transition disabled:opacity-40 flex-shrink-0"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAssistant;
